/**
 * Check a room on the web (core roomcheck.ts decides everything; docs/ROOMS.md). Pure helpers so the screens stay thin:
 * which API scope a session checks in, where a room's check lives, and how a basis reads on one line.
 */
import { sourceNames, type Basis, type ItemTier, type Location, type Organization, type RoomListStatus } from "@logwave/core";
import type { RoomScopeArg } from "../api.ts";
import { isHomeOrg } from "../api.ts";

/** A home checks under /homes/:orgId (the home is the location); a business at its current location. */
export function roomScopeFor(s: { org: Organization; location: Pick<Location, "id"> }): RoomScopeArg {
  return isHomeOrg(s.org) ? { orgId: s.org.id, home: true } : { orgId: s.org.id, locationId: s.location.id };
}

/** The same paths in a business and a home: the flow resumes from the link (a reload lands where you were). */
export const roomCheckPath = (room: string) => `/rooms/check/${encodeURIComponent(room)}`;
export const roomContentsPath = (room: string) => `/rooms/check/${encodeURIComponent(room)}/contents`;

export const ROOM_STATUS_TONE: Record<RoomListStatus, "go" | "warn" | "neutral"> = { clear: "go", open: "warn", not_clear: "warn", never: "neutral" };
export const TIER_TONE: Record<ItemTier, "stop" | "info" | "neutral"> = { needed: "stop", recommended: "info", optional: "neutral" };

/**
 * Info line for an item or a step: the rule for law, OSHA and the maker ("Why · Required by law · Food Code 4-602.11");
 * "Suggested" with where it's from, or its reason when nothing sources it.
 */
export function basisLine(x: { basis: Basis; basisLine: string; citation?: string; basisReason?: string }): { label: string; value: string } {
  if (x.basis === "sense") return { label: "Suggested", value: x.citation ? `From ${x.citation}` : x.basisReason ?? "No rule found; a common-sense best practice." };
  if (x.basis === "maker") return { label: "Why", value: x.citation ? `${x.basisLine} · ${x.citation}` : x.basisLine };
  return { label: "Why", value: x.basisLine };
}

/**
 * A step's own source line (owner, 2026-10-05: "show only the step's own sources, not the guide-wide list"): its `cite`
 * ("Source: Whirlpool, Samsung"), "Our advice", or, when nothing sources the step, plainly that. A law, OSHA or maker step
 * keeps its rule line.
 */
export function stepInfoLine(x: { basis: Basis; basisLine: string; citation?: string; basisReason?: string; cite?: string[] | "advice" }): { label: string; value: string } | null {
  if (x.basis !== "sense") return basisLine(x);
  if (x.cite === "advice") return { label: "Suggested", value: "Our advice" };
  if (Array.isArray(x.cite) && x.cite.length) return { label: "Source", value: sourceNames(x.cite) };
  return { label: "Suggested", value: "No source found for this step; a common-sense habit." };
}
